// @spec SHL-STR-020
// @spec SHL-STR-021
'use client';

import { Command } from 'cmdk';

import type { AppCommand } from './command-registry';

import { cn } from '@/lib/utils';

export interface CommandItemProps {
  /** Command configuration */
  command: AppCommand;
  /** Called when the command is selected */
  onSelect: () => void;
}

/**
 * Single command item in the command palette.
 *
 * Renders with:
 * - Icon on the left (16x16, muted color)
 * - Label in the center
 * - Keyboard shortcut badge on the right (if exists)
 * - Proper disabled state with aria-disabled and visual differentiation
 */
export function CommandItem({ command, onSelect }: CommandItemProps) {
  const Icon = command.icon;
  const isDisabled = command.disabled ?? false;

  return (
    <Command.Item
      value={command.id}
      onSelect={onSelect}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm outline-none',
        'aria-selected:bg-accent aria-selected:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed'
      )}
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="flex-1 truncate">{command.label}</span>
      {command.shortcut && (
        <kbd className="pointer-events-none hidden select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
          {command.shortcut}
        </kbd>
      )}
    </Command.Item>
  );
}
